export interface RetargetingRule {
  id: number;
  "workspace-id": number;
  "owner-id": number;
  name: string;
  rules: RetargetingRuleNodeRoot;
  "fb-ad-account-id": string;
  "fb-custom-audience-id": string;
  progress: string;
  "created-at": string;
  "updated-at": string;
}

export interface RetargetingRuleNodeJunction {
  mode: RetargetingRuleNodeMode;
  type: "and" | "or";
  retention: number;
  children: RetargetingRuleNode[];
}

export enum RetargetingRuleNodeMode {
  INCLUDE = "include",
  EXCLUDE = "exclude",
}

export interface RetargetingRuleNodeURL {
  operator: "contains" | "not-contains";
  field: "url";
  value: string;
}

export type RetargetingRuleNode =
  | RetargetingRuleNodeJunction
  | RetargetingRuleNodeURL;

export interface RetargetingRuleNodeRoot {
  children: RetargetingRuleNodeJunction[];
}

const defaultURLNode: RetargetingRuleNodeURL = {
  operator: "contains",
  field: "url",
  value: "",
};

export function pushRootNodeChild(
  root: RetargetingRuleNodeRoot,
  mode: RetargetingRuleNodeMode,
): RetargetingRuleNodeRoot {
  return {
    children: [
      ...root.children,
      {
        mode,
        type: "and",
        retention: 30,
        children: [defaultURLNode],
      },
    ],
  };
}

export function replaceRootNodeChild(
  node: RetargetingRuleNodeRoot,
  child: RetargetingRuleNodeJunction,
  index: number,
): RetargetingRuleNodeRoot {
  const pre = node.children.slice(0, index);
  const post = node.children.slice(index + 1);
  return { ...node, children: [...pre, child, ...post] };
}

export function removeRootNodeChild(
  node: RetargetingRuleNodeRoot,
  index: number,
): RetargetingRuleNodeRoot {
  const pre = node.children.slice(0, index);
  const post = node.children.slice(index + 1);
  return { ...node, children: [...pre, ...post] };
}

export function isJunctionNode(
  node: RetargetingRuleNode,
): node is RetargetingRuleNodeJunction {
  return ["and", "or"].includes((node as RetargetingRuleNodeJunction).type);
}

export function setJunctionNodeType(
  node: RetargetingRuleNodeJunction,
  type: string,
): RetargetingRuleNodeJunction {
  if (type !== "and" && type !== "or") {
    throw new Error(`setJunctionNodeType: invalid type ${type}`);
  }
  return { ...node, type };
}

export function replaceJunctionNodeChild(
  node: RetargetingRuleNodeJunction,
  child: RetargetingRuleNode,
  index: number,
): RetargetingRuleNodeJunction {
  const pre = node.children.slice(0, index);
  const post = node.children.slice(index + 1);
  return { ...node, children: [...pre, child, ...post] };
}

export function removeJunctionNodeChild(
  node: RetargetingRuleNodeJunction,
  index: number,
): RetargetingRuleNodeJunction {
  const pre = node.children.slice(0, index);
  const post = node.children.slice(index + 1);
  return { ...node, children: [...pre, ...post] };
}

export function pushJunctionNodeChild(
  node: RetargetingRuleNodeJunction,
): RetargetingRuleNodeJunction {
  const child = defaultURLNode;
  return { ...node, children: [...node.children, child] };
}

export function isURLNode(
  node: RetargetingRuleNode,
): node is RetargetingRuleNodeURL {
  return (node as RetargetingRuleNodeURL).field === "url";
}

export function setURLNodeOperator(
  node: RetargetingRuleNodeURL,
  operator: string,
): RetargetingRuleNodeURL {
  if (operator !== "contains" && operator !== "not-contains") {
    throw new Error(`setURLNodeOperator: invalid operator ${operator}`);
  }
  return { ...node, operator };
}
